// This Mac's runner's local reads (offline design §3, Poulpe-828), asked of
// the Mac app (mac/Sources/PoulpeMacUI/LocalReads.swift), which runs
// `poulpe-runner local-read` for the page: a page cannot open the runner's
// Unix socket. The page posts `{localRead: {id, document, read, …}}` and the
// shell answers a `poulpe-local-read` event: `{id, text}`, the runner's JSON
// reply (crates/poulpe-protocol/src/local_reads.rs), or `{id}` alone when
// this Mac's runner gives nothing: none is installed, it is older than
// Poulpe-827, its socket is missing, or it took too long. Nothing, a refusal
// and a malformed reply all read as null here: no overlay, and no error
// beyond the app's own "Offline".
import { hasShell, inMacShell, tellShell, type ShellMessage } from '../lib/shell'
import { itemOf } from './transcript'
import type { CheckProgress, LiveCheck, Reply, RunnerJob, TranscriptItem } from './types'
import { uuid4 } from './uuid'

const ANSWER = 'poulpe-local-read'
/** The app kills `local-read` after 5 s; past this, the shell is not answering. */
const PATIENCE = 10_000
const WRITTEN = 'poulpe-local-write'
/** The app waits 125 s for a write: a prompt to an agent that cannot be steered is had at its turn's end. */
const WRITE_PATIENCE = 130_000

export type LocalRead = { read: 'sessions' } | { read: 'checks' } | { read: 'transcript'; session: string; after?: string }

/** A page of a session's transcript, as this Mac's runner holds it: its ids and times are the runner's own. */
export interface LocalPage { items: TranscriptItem[]; more: boolean }

/** A check of this Mac's runner, as its local reads give it. */
export interface LocalCheck { job: string; progress: CheckProgress | null; ended: boolean }

/**
 * The runner installed on this Mac, whose local reads the Mac app gives; null
 * outside the app or when none is installed. The shell names it on <html>
 * (`data-claude-api-key`, Poulpe-718), read again for each document.
 */
export function localRunner(): string | null {
  if (typeof document === 'undefined' || !inMacShell() || !hasShell()) return null
  return document.documentElement.dataset.claudeApiKey || null
}

/** Asks the shell for one read; the runner's reply as an object, or null for nothing. */
export function readLocally(request: LocalRead): Promise<Record<string, unknown> | null> {
  return askShell((id, document) => ({ localRead: { id, document, ...request } }), ANSWER, PATIENCE, reply => reply.reply === request.read)
}

/**
 * Posts one request to the shell, as `message` writes it, and waits for its `event` with
 * the same id; the runner's JSON reply when `fits` takes it, null for
 * anything else or nothing within `patience`.
 */
function askShell(message: (id: string, document: string) => ShellMessage, event: string, patience: number, fits: (reply: Record<string, unknown>) => boolean): Promise<Record<string, unknown> | null> {
  const document = globalThis.document?.documentElement.dataset.sessionDocument
  if (!localRunner() || !document) return Promise.resolve(null)
  return new Promise(resolve => {
    const id = uuid4()
    const done = (value: Record<string, unknown> | null) => {
      window.removeEventListener(event, listener)
      window.clearTimeout(timeout)
      resolve(value)
    }
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail
      if (typeof detail !== 'object' || detail === null || (detail as { id?: unknown }).id !== id) return
      const text = (detail as { text?: unknown }).text
      if (typeof text !== 'string') return done(null)
      try {
        const reply: unknown = JSON.parse(text)
        done(typeof reply === 'object' && reply !== null && fits(reply as Record<string, unknown>) ? reply as Record<string, unknown> : null)
      } catch {
        done(null)
      }
    }
    const timeout = window.setTimeout(() => done(null), patience)
    window.addEventListener(event, listener)
    tellShell(message(id, document))
  })
}

/** This Mac's runner's sessions, and whether each takes a write here: its owner started it. Null for nothing. */
export async function localSessions(): Promise<{ session: string; byOwner: boolean }[] | null> {
  const reply = await readLocally({ read: 'sessions' })
  if (!reply || !Array.isArray(reply.sessions)) return null
  return reply.sessions.flatMap((value: unknown) => {
    if (typeof value !== 'object' || value === null) return []
    const { session, by_owner: byOwner } = value as Record<string, unknown>
    // A runner older than its writes says no `by_owner`: no write here.
    return typeof session === 'string' && session !== '' ? [{ session, byOwner: byOwner === true }] : []
  })
}

/**
 * A write to this Mac's runner while offline (offline design §4,
 * Poulpe-830): a prompt, or an answer to a question its session waits on,
 * for a session its owner started.
 */
export type LocalWrite =
  | { write: 'prompt'; session: string; prompt: string }
  | { write: 'answer'; session: string; question: string; version: number; replies: Reply[] }

/** How a write fared: the agent has it, the runner refused it (in its words), or nothing came. */
export type LocalWriteOutcome = { done: true } | { done: false; reason: string | null }

/** The most UTF-8 bytes of a prompt the app hands on (`LocalWrite.maxPrompt`). */
export const MAX_LOCAL_PROMPT = 60 * 1024

/** Asks the shell for one write. */
export async function writeLocally(write: LocalWrite): Promise<LocalWriteOutcome> {
  const reply = await askShell((id, document) => ({ localWrite: { id, document, ...write } }), WRITTEN, WRITE_PATIENCE, reply => reply.reply === 'done' || reply.reply === 'refused')
  if (reply?.reply === 'done') return { done: true }
  return { done: false, reason: reply && typeof reply.reason === 'string' ? reply.reason : null }
}

/** A page of `session`'s transcript after the runner's item `after`, or from its oldest; null for nothing. */
export async function localTranscript(session: string, after: string | null): Promise<LocalPage | null> {
  const reply = await readLocally(after ? { read: 'transcript', session, after } : { read: 'transcript', session })
  if (!reply || !Array.isArray(reply.items)) return null
  const items = reply.items.map(itemOf).filter((item): item is TranscriptItem => item !== null)
  return { items, more: reply.more === true }
}

/** This Mac's runner's checks; null for nothing. */
export async function localChecks(): Promise<LocalCheck[] | null> {
  const reply = await readLocally({ read: 'checks' })
  if (!reply || !Array.isArray(reply.checks)) return null
  return reply.checks.flatMap((value: unknown) => {
    if (typeof value !== 'object' || value === null) return []
    const { job, progress, ended } = value as Record<string, unknown>
    if (typeof job !== 'string' || job === '') return []
    const known = typeof progress === 'object' && progress !== null ? progress as CheckProgress : null
    return [{ job, progress: known, ended: ended === true }]
  })
}

/** The runner's checks as live checks, their project and session from the jobs the device kept. */
export function asLiveChecks(checks: readonly LocalCheck[], jobs: ReadonlyMap<string, RunnerJob>): LiveCheck[] {
  return checks.map(check => {
    const job = jobs.get(check.job)
    return {
      job: check.job, project: job?.project_id ?? '', session: job?.session_id ?? null,
      proposal: null, landing: null, reported_at: null, usual_ms: null, progress: check.progress,
    }
  })
}
